// PrivateShowCircleProgressBar.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import CircleProgressBar from '../component/CircleProgressBar.jsx';
import { privateShowStatus } from '../resource/liveStreamConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { color, textColor } from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';

export const PrivateShowCircleProgressBar = ({
  status = null,
  progress = 0,
  target = 0,
  size = 56,
}) => {
  const { t } = useTranslation();
  const progressRatio = Math.round((progress / target) * 100) || 0;
  if (privateShowStatus.PERFORMING === status) {
    return (
      <StyledPrivateShowCircleProgressBar>
        <FilledCircle size={size} backgroundColor={color.carmine} />
        <ProgressValue color={textColor.white} size={size} isPerforming>
          {t('show_time', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </ProgressValue>
      </StyledPrivateShowCircleProgressBar>
    );
  }
  if (privateShowStatus.REFUNDING === status) {
    return (
      <StyledPrivateShowCircleProgressBar>
        <CircleProgressBar
          value={progressRatio}
          size={size}
          backgroundStrokeWidth={3}
          barStrokeWidth={3}
          backgroundColor={color.grey['500']}
          barColor={color.carmine}
        />
        <ProgressValue color={color.grey['500']} size={size}>
          {t('popup_not_reached', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </ProgressValue>
      </StyledPrivateShowCircleProgressBar>
    );
  }
  if (privateShowStatus.PREPARING === status) {
    return (
      <StyledPrivateShowCircleProgressBar>
        <CircleProgressBar
          value={100}
          size={size}
          backgroundStrokeWidth={3}
          barStrokeWidth={3}
          backgroundColor={color.grey['500']}
          barColor={color.carmine}
        />
        <ProgressValue color={color.carmine} size={size}>
          {t('popup_goal', {
            ns: TranslationNamespace.LIVESTREAM,
          })}
        </ProgressValue>
      </StyledPrivateShowCircleProgressBar>
    );
  }
  return (
    <StyledPrivateShowCircleProgressBar>
      <CircleProgressBar
        value={progressRatio}
        size={size}
        backgroundStrokeWidth={3}
        barStrokeWidth={3}
        backgroundColor={color.grey['500']}
        barColor={color.carmine}
      />
      <ProgressValue color={textColor.white} size={size}>
        {progressRatio}%
      </ProgressValue>
    </StyledPrivateShowCircleProgressBar>
  );
};

PrivateShowCircleProgressBar.propTypes = {
  status: PropTypes.string,
  target: PropTypes.number,
  progress: PropTypes.number,
  size: PropTypes.number,
};

const StyledPrivateShowCircleProgressBar = styled.div`
  font-size: 0px;
  position: relative;
`;

const FilledCircle = styled.div.attrs(({ size, backgroundColor }) => ({
  style: {
    width: `${size}px`,
    height: `${size}px`,
    backgroundColor: backgroundColor,
  },
}))`
  border-radius: 50%;
`;

const ProgressValue = styled.div.attrs(({ color, size, isPerforming }) => ({
  style: {
    color: color ? color : textColor.white,
    top: `${size / 2}px`,
    whiteSpace: isPerforming ? 'pre-line' : 'nowrap',
  },
}))`
  ${TextEllipsis};
  position: absolute;
  left: 50%;
  padding: 4px;
  width: 100%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
`;

export default PrivateShowCircleProgressBar;
